import React, { useEffect, useState, useRef, ComponentType } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { UsersIcon, HomeIcon, DollarSignIcon, HeartHandshakeIcon, ChevronDownIcon, FileTextIcon, ExternalLinkIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { HONDURAS_REPORTS, HONDURAS_FACT_NUMBERS } from '../data/honduras-reports';
interface CounterCardProps {
  value: number;
  suffix: string;
  label: string;
  sublabel: string;
  borderColor: string;
  accentColor: string;
  Icon: ComponentType<{
    className?: string;
  }>;
  delay: number;
  stripeColors: string[];
}
function useCounter(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}
function formatCount(n: number): string {
  if (n >= 1000000000) {
    const val = n / 1000000000;
    return (Number.isInteger(val) ? val.toString() : val.toFixed(1)) + 'B';
  }
  if (n >= 1000000) {
    const val = n / 1000000;
    return (Number.isInteger(val) ? val.toString() : val.toFixed(1)) + 'M';
  }
  if (n >= 1000) {
    return (n / 1000).toFixed(0) + 'K';
  }
  return n.toLocaleString();
}
function CounterCard({
  value,
  suffix,
  label,
  sublabel,
  borderColor,
  accentColor,
  Icon,
  delay,
  stripeColors
}: CounterCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useCounter(value, 2000, inView);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50,
    scale: 0.9
  }} animate={inView ? {
    opacity: 1,
    y: 0,
    scale: 1
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative bg-white rounded-2xl shadow-xl overflow-hidden group card-hover" data-id="element-464" data-mp="3gp24q9kvv1g4">
      {/* Flag stripe top border */}
      <div className="h-2 w-full flex" data-id="element-465" data-mp="264xkcppwe1f2">
        {stripeColors.map((color, i) => <div key={i} className="flex-1" style={{
        backgroundColor: color
      }} data-id="element-466" data-mp="215ug37hqrz85" data-mpi={i} />)}
      </div>

      {/* Thick colored left accent */}
      <div className={`absolute left-0 top-2 bottom-0 w-1.5 ${borderColor}`} data-id="element-467" data-mp="1s5o3roxs55ak" />

      <div className="p-8 pl-10" data-id="element-468" data-mp="kr2x1l6hk4bi">
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${accentColor} bg-opacity-10 mb-4`} data-id="element-469" data-mp="ksw1cttmmcgc">
          <Icon className={`w-6 h-6 ${accentColor}`} data-id="element-470" data-mp="2sdcpty1y3a7s" />
        </div>

        {/* Counter */}
        <div className="flex items-end gap-1 mb-2" data-id="element-471" data-mp="2weo9lmy51182">
          <span className={`font-bebas text-6xl sm:text-7xl leading-none tracking-wider ${accentColor}`} data-id="element-472" data-mp="2wf7qd0c1xjki">
            {formatCount(count)}
          </span>
          <span className={`font-bebas text-4xl leading-none ${accentColor} mb-1`} data-id="element-473" data-mp="365j6rmcx9l8a">
            {suffix}
          </span>
        </div>

        {/* Label */}
        <h3 className="font-bebas text-2xl text-haa-navy tracking-wider leading-none mb-1" data-id="element-474" data-mp="3g25po5lou27o">
          {label}
        </h3>
        <p className="font-nunito text-gray-500 text-sm" data-id="element-475" data-mp="3k5bv5b0z7pd3">{sublabel}</p>
      </div>

      {/* Decorative corner */}
      <div className={`absolute bottom-0 right-0 w-20 h-20 rounded-tl-full opacity-10 ${borderColor}`} data-id="element-476" data-mp="3p2ttk7hdam6u" />
    </motion.div>;
}
export function CommunityStrength() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const [factsOpen, setFactsOpen] = useState(false);
  const [hondurasFactsOpen, setHondurasFactsOpen] = useState(false);
  const {
    t
  } = useLanguage();
  return <section id="community" className="bg-gray-50 py-16 lg:py-24" aria-labelledby="strength-heading" data-id="element-477" data-mp="19d22jh7e36c9">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-478" data-mp="14fadqw21k0ca">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16" data-id="element-479" data-mp="gnv6coveqsu6">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-480" data-mp="gmbo4sq6y7pr">
            <span className="font-nunito text-haa-blue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-481" data-mp="3u1zxhgnuyiui">
              {t('community.label')}
            </span>
            <h2 id="strength-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider mt-2" data-id="element-482" data-mp="24lxdu5nnjyd0">
              {t('community.heading')}
            </h2>
            <div className="flex items-center justify-center gap-3 mt-4" data-id="element-483" data-mp="1upkgmxswf2di">
              <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-484" data-mp="1zmkpfgh4uj8b" />
              <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-485" data-mp="1zkzxlym7dee5" />
              <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-486" data-mp="2ndgg2a9ur8dz" />
            </div>
            <p className="font-nunito text-gray-600 text-lg mt-6 max-w-2xl mx-auto" data-id="element-487" data-mp="2saozee6qligt">
              {t('community.description')}
            </p>
          </motion.div>
        </div>

        {/* Counter Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-488" data-mp="2ic0rw9cdolrd">
          <CounterCard value={1800000} suffix="+" label={t('community.card1.label')} sublabel={t('community.card1.sublabel')} borderColor="bg-haa-skyblue text-haa-skyblue" accentColor="text-haa-skyblue" Icon={UsersIcon} delay={0} stripeColors={['#4A90D9', '#ffffff', '#4A90D9']} data-id="element-489" data-mp="12wads2r0mbpj" />
          <CounterCard value={10400000000} suffix="+" label={t('community.card2.label')} sublabel={t('community.card2.sublabel')} borderColor="bg-haa-midblue text-haa-midblue" accentColor="text-haa-midblue" Icon={DollarSignIcon} delay={0.1} stripeColors={['#1565C0', '#ffffff', '#1565C0']} data-id="element-490" data-mp="2zu7wfoctfc8e" />
          <CounterCard value={31} suffix="%" label={t('community.card3.label')} sublabel={t('community.card3.sublabel')} borderColor="bg-haa-blue text-haa-blue" accentColor="text-haa-blue" Icon={HomeIcon} delay={0.2} stripeColors={['#003F87', '#ffffff', '#003F87']} data-id="element-491" data-mp="2gx6p3wvsh01a" />
          <CounterCard value={100} suffix="+" label={t('community.card4.label')} sublabel={t('community.card4.sublabel')} borderColor="bg-haa-navy text-haa-navy" accentColor="text-haa-navy" Icon={HeartHandshakeIcon} delay={0.3} stripeColors={['#001A3E', '#ffffff', '#001A3E']} data-id="element-492" data-mp="img06vq0cxuz" />
        </div>

        {/* Honduras Facts Dropdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.45,
        duration: 0.5
      }} className="mt-10 max-w-4xl mx-auto" data-id="element-493" data-mp="3pyrydd47zcn">
          <div className="rounded-2xl border-l-4 border-haa-blue bg-white shadow-lg overflow-hidden" data-id="element-494" data-mp="1p31hc0fgjmmr">
            <button onClick={() => setHondurasFactsOpen(!hondurasFactsOpen)} aria-expanded={hondurasFactsOpen} className="w-full flex items-center justify-center px-6 py-5 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-495" data-mp="2a72jdbvtx6ff">
              <div className="flex items-center gap-3" data-id="element-496" data-mp="2f4vriagp0aic">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-497" data-mp="2tutisz9x5gbr">
                  <FileTextIcon className="w-5 h-5 text-white" data-id="element-498" data-mp="2oycjra16lhjw" />
                </div>
                <span className="font-bebas text-2xl tracking-wider text-haa-navy" data-id="element-499" data-mp="32vhnv4hc46ci">
                  {t('community.hondurasFacts')}
                </span>
                <motion.div animate={{
                rotate: hondurasFactsOpen ? 180 : 0
              }} transition={{
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} className="flex-shrink-0" data-id="element-500" data-mp="18hgpsltlzndh">
                  <ChevronDownIcon className="w-6 h-6 text-haa-blue" data-id="element-501" data-mp="3k40qigwf7tr9" />
                </motion.div>
              </div>
            </button>
            <AnimatePresence initial={false} data-id="element-502" data-mp="36c49b7waj49n">
              {hondurasFactsOpen && <motion.div key="honduras-facts-content" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-503" data-mp="3adfqqq50gj6r">
                  <div className="border-t border-gray-100 px-6 py-5" data-id="element-504" data-mp="3m2baj3u9tooe">
                    <h4 className="font-bebas text-lg tracking-wider text-haa-blue mb-3" data-id="element-505" data-mp="3m105z1t04cw8">
                      {t('community.hondurasFactsSubheading')}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-506" data-mp="3c5gcem1wtqan">
                      {HONDURAS_FACT_NUMBERS.map(num => <div key={num} className="bg-blue-50 rounded-xl px-4 py-3 border border-blue-100" data-id="element-507" data-mp="8nohaysh8q88">
                            <p className="font-nunito text-xs text-gray-500 uppercase tracking-wider font-semibold" data-id="element-508" data-mp="3vsec3sfgkayc">
                              {t(`community.fact${num}.label`)}
                            </p>
                            <p className="font-nunito text-sm text-haa-navy font-bold mt-0.5" data-id="element-509" data-mp="2kcfxza5fayq9">
                              {t(`community.fact${num}.value`)}
                            </p>
                          </div>)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Honduras Reports & Resources Dropdown */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        delay: 0.5,
        duration: 0.5
      }} className="mt-10 max-w-4xl mx-auto" data-id="element-510" data-mp="2lu4rtwzj73iq">
          <div className="rounded-2xl border-l-4 border-haa-blue bg-white shadow-lg overflow-hidden" data-id="element-511" data-mp="233wvpyu91dc1">
            <button onClick={() => setFactsOpen(!factsOpen)} aria-expanded={factsOpen} className="w-full flex items-center justify-center px-6 py-5 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" data-id="element-512" data-mp="1fdj1q2s3t4et">
              <div className="flex items-center gap-3" data-id="element-513" data-mp="243dwr58l45w7">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-514" data-mp="28vowa6zexpo8">
                  <FileTextIcon className="w-5 h-5 text-white" data-id="element-515" data-mp="2du0308uevk28" />
                </div>
                <span className="font-bebas text-2xl tracking-wider text-haa-navy" data-id="element-516" data-mp="1h5kdudv7rcpn">
                  {t('community.reportsResources')}
                </span>
                <motion.div animate={{
                rotate: factsOpen ? 180 : 0
              }} transition={{
                duration: 0.25,
                ease: [0.16, 1, 0.3, 1]
              }} className="flex-shrink-0" data-id="element-517" data-mp="1q575w921z661">
                  <ChevronDownIcon className="w-6 h-6 text-haa-blue" data-id="element-518" data-mp="36i4dr8ruanbs" />
                </motion.div>
              </div>
            </button>

            <AnimatePresence initial={false} data-id="element-519" data-mp="uxwtfmf50j8y">
              {factsOpen && <motion.div key="facts-content" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.35,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-520" data-mp="px9558iamgif">
                  <div className="border-t border-gray-100 px-6 py-5" data-id="element-521" data-mp="3tf0u0e14ukce">
                    {/* Reports & Resources */}
                    <div data-id="element-522" data-mp="1wj380vc14yjm">
                      <h4 className="font-bebas text-lg tracking-wider text-haa-blue mb-3" data-id="element-523" data-mp="1rmcm3rdjn8qa">
                        {t('community.reportsResources')}
                      </h4>
                      <div className="space-y-2" data-id="element-524" data-mp="1zky2dizyr1lw">
                        {HONDURAS_REPORTS.map(report => <a key={report.num} href={report.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 hover:bg-blue-50 hover:border-blue-200 transition-colors group" data-id="element-525" data-mp="1pomk8cdozhjj">
                            <div className="flex-1 min-w-0" data-id="element-526" data-mp="16ywkdav2o5kr">
                              <p className="font-nunito font-bold text-sm text-haa-navy group-hover:text-haa-blue transition-colors leading-snug" data-id="element-527" data-mp="1bx7qpbm5rxy8">
                                {t(`community.report${report.num}.title`)}
                              </p>
                              <p className="font-nunito text-xs text-gray-500 leading-relaxed mt-0.5" data-id="element-528" data-mp="11xqq38lrka94">
                                {t(`community.report${report.num}.description`)}
                              </p>
                            </div>
                            <ExternalLinkIcon className="w-4 h-4 text-gray-300 group-hover:text-haa-blue flex-shrink-0 mt-0.5 transition-colors" data-id="element-529" data-mp="sb67uz08uvu0" />
                          </a>)}
                      </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Bottom decorative element — Honduran flag: blue | white | blue */}
        <motion.div initial={{
        opacity: 0,
        scaleX: 0
      }} animate={titleInView ? {
        opacity: 1,
        scaleX: 1
      } : {}} transition={{
        duration: 1,
        delay: 0.5
      }} className="mt-16 h-1 w-full rounded-full overflow-hidden flex" data-id="element-530" data-mp="1b14dl8gkkcuh">
          <div className="flex-1 bg-haa-blue" data-id="element-531" data-mp="howk722njvez" />
          <div className="flex-1 bg-white border-y border-gray-200" data-id="element-532" data-mp="hpphzdjmvakz" />
          <div className="flex-1 bg-haa-blue" data-id="element-533" data-mp="mo0rfn60nj2o" />
        </motion.div>
      </div>
    </section>;
}